import React, { useEffect, useState } from 'react'
import http from '../../api/http'
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, IconButton, CircularProgress, FormControl, InputLabel, Select, MenuItem } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'

interface Translation {
  _id: string
  locale: string
  namespace: string
  key: string
  text: string
}

const Translations: React.FC = () => {
  const [data, setData] = useState<Translation[]>([])
  const [count, setCount] = useState<number>(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [keyFilter, setKeyFilter] = useState('')
  const [namespaceFilter, setNamespaceFilter] = useState('')
  const [localeFilter, setLocaleFilter] = useState('')
  const [localeOptions, setLocaleOptions] = useState<string[]>([])
  const [openModal, setOpenModal] = useState(false)
  const [editingTranslation, setEditingTranslation] = useState<Translation | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Translation | null>(null)
  const [openDelete, setOpenDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [clearingCache, setClearingCache] = useState(false)
  const [openStats, setOpenStats] = useState(false)
  const [statsData, setStatsData] = useState<any>(null)
  const [loadingStats, setLoadingStats] = useState(false)

  // create form state
  const [newKey, setNewKey] = useState('')
  const [newNamespace, setNewNamespace] = useState('')
  const [newLocale, setNewLocale] = useState('')
  const [newText, setNewText] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  const fetchTranslations = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await http.get('/api/translation/translations', {
        ...(keyFilter ? { key: keyFilter } : {}),
        ...(namespaceFilter ? { namespace: namespaceFilter } : {}),
        ...(localeFilter ? { locale: localeFilter } : {}),
      })

      const resData = response.data
      setData(resData?.data || [])
      setCount(resData?.count || 0)

      const localesSet = new Set<string>((resData?.data || []).map((t: Translation)=>t.locale))
      setLocaleOptions(Array.from(localesSet))
    } catch (err: any) {
      setError(err.message || 'Error fetching translations')
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async () => {
    try {
      setCreating(true)
      setCreateError(null)

      if (editingTranslation) {
        await http.put(`/api/translation/translations/${editingTranslation._id}`, {
          text: newText,
        })
      } else {
        await http.post('/api/translation/translations', {
          key: newKey,
          namespace: newNamespace,
          locale: newLocale,
          text: newText,
        })
      }

      // reset inputs
      setNewKey('')
      setNewNamespace('')
      setNewLocale('')
      setNewText('')
      setEditingTranslation(null)

      // refresh list
      fetchTranslations()
      setOpenModal(false)
    } catch (err: any) {
      setCreateError(err.response?.data?.message || err.message || 'Error saving translation')
    } finally {
      setCreating(false)
    }
  }

  useEffect(() => {
    fetchTranslations()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading translations...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-red-500">{error}</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h1 className="text-2xl font-bold text-gray-900">Translations</h1>
            <div className="flex items-center align-items-center gap-4">
              <span className="text-gray-600 text-sm">Total: {count}</span>
              <Button variant="contained" size="small"
                sx={{ backgroundColor: '#3baab0', '&:hover': { backgroundColor: '#32969b' } }}
                onClick={() => setOpenModal(true)}
              >
                Add
              </Button>
              <Button variant="contained" size="small"
                sx={{ backgroundColor: '#3baab0', '&:hover': { backgroundColor: '#32969b' } }}
                onClick={async () => {
                  try {
                    setClearingCache(true)
                    await http.remove('/api/translation/cache', {})
                  } catch (err) {
                    console.error(err)
                  } finally {
                    setClearingCache(false)
                  }
                }}
                disabled={clearingCache}
              >
                {clearingCache ? 'Clearing...' : 'Clear cache'}
              </Button>
              <Button variant="outlined" size="small"
                sx={{ borderColor: '#3baab0', color: '#3baab0', '&:hover': { borderColor: '#32969b', backgroundColor: 'rgba(59,170,176,0.04)' } }}
                onClick={async () => {
                  try {
                    setLoadingStats(true)
                    const res = await http.get('/api/translation/cache/stats')
                    setStatsData(res.data?.data || null)
                    setOpenStats(true)
                  } catch (err) {
                    console.error(err)
                  } finally {
                    setLoadingStats(false)
                  }
                }}
              >
                {loadingStats ? <CircularProgress size={18} /> : 'Cache stats'}
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50 flex flex-wrap gap-4">
            <input
              type="text"
              placeholder="Key"
              value={keyFilter}
              onChange={(e) => setKeyFilter(e.target.value)}
              className="border px-3 py-2 rounded w-32"
            />
            <input
              type="text"
              placeholder="Namespace"
              value={namespaceFilter}
              onChange={(e) => setNamespaceFilter(e.target.value)}
              className="border px-3 py-2 rounded w-32"
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Locale</InputLabel>
              <Select
                value={localeFilter}
                label="Locale"
                onChange={(e) => {
                  setLocaleFilter(e.target.value as string)
                  fetchTranslations()
                }}
              >
                <MenuItem value=""><em>All</em></MenuItem>
                {localeOptions.map((loc)=>(
                  <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <button
              onClick={fetchTranslations}
              className="btn"
            >
              Search
            </button>
            <button
              onClick={() => {
                setKeyFilter('')
                setNamespaceFilter('')
                setLocaleFilter('')
                fetchTranslations()
              }}
              className="btn ml-2 bg-gray-200 text-gray-800 hover:bg-gray-300"
            >
              Reset
            </button>
          </div>

          {/* Modal for create */}
          <Dialog open={openModal} onClose={() => {
              setOpenModal(false)
              setEditingTranslation(null)
            }} maxWidth="md" fullWidth>
            <DialogTitle>{editingTranslation ? 'Edit Translation' : 'Add Translation'}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField label="Key" value={newKey} onChange={(e)=>setNewKey(e.target.value)} fullWidth disabled={!!editingTranslation} />
              <TextField label="Namespace" value={newNamespace} onChange={(e)=>setNewNamespace(e.target.value)} fullWidth disabled={!!editingTranslation} />
              <TextField label="Locale" value={newLocale} onChange={(e)=>setNewLocale(e.target.value)} fullWidth disabled={!!editingTranslation} />
              <TextField label="Text" value={newText} onChange={(e)=>setNewText(e.target.value)} multiline rows={3} fullWidth />
            </DialogContent>
            <DialogActions>
              {createError && <span style={{color:'red', marginRight:'auto'}}>{createError}</span>}
              <Button onClick={()=>setOpenModal(false)}
                sx={{ color: '#3baab0', '&:hover': { backgroundColor: 'rgba(59,170,176,0.08)' } }}
              >
                Cancel
              </Button>
              <Button onClick={handleCreate} variant="contained" disabled={creating}
                sx={{ backgroundColor: '#3baab0', '&:hover': { backgroundColor: '#32969b' } }}
              >
                {creating ? 'Saving...' : editingTranslation ? 'Update' : 'Create'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Delete confirm dialog */}
          <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
            <DialogTitle>Delete Translation</DialogTitle>
            <DialogContent>
              Are you sure you want to delete translation "{deleteTarget?.key}"?
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDelete(false)}
                sx={{ color: '#3baab0', '&:hover': { backgroundColor: 'rgba(59,170,176,0.08)' } }}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  if (!deleteTarget) return
                  try {
                    setDeleting(true)
                    await http.remove(`/api/translation/translations/${deleteTarget._id}`, {})
                    setOpenDelete(false)
                    setDeleteTarget(null)
                    fetchTranslations()
                  } catch (err) {
                    console.error(err)
                  } finally {
                    setDeleting(false)
                  }
                }}
                disabled={deleting}
                variant="contained"
                sx={{ backgroundColor: '#e53935', '&:hover': { backgroundColor: '#d32f2f' } }}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Stats dialog */}
          <Dialog open={openStats} onClose={() => setOpenStats(false)}>
            <DialogTitle>Cache statistics</DialogTitle>
            <DialogContent dividers>
              {statsData ? (
                <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(statsData, null, 2)}</pre>
              ) : (
                'No stats available'
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenStats(false)}
                sx={{ color: '#3baab0', '&:hover': { backgroundColor: 'rgba(59,170,176,0.08)' } }}
              >
                Close
              </Button>
            </DialogActions>
          </Dialog>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Locale</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Namespace</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Key</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Text</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.map((t) => (
                  <tr key={t._id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{t.locale}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{t.namespace}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{t.key}</td>
                    <td className="px-6 py-4 whitespace-pre-wrap text-sm text-gray-900">{t.text}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <IconButton size="small" onClick={() => {
                        setEditingTranslation(t)
                        setNewKey(t.key)
                        setNewNamespace(t.namespace)
                        setNewLocale(t.locale)
                        setNewText(t.text)
                        setOpenModal(true)
                      }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => { setDeleteTarget(t); setOpenDelete(true); }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Translations
